import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableViewIcon from '@mui/icons-material/TableViewOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { downloadReport, fetchReportPdf, reportFilename, ReportFilters } from '../../api/reports';
import { listLookup } from '../../api/masterData';
import { listStudentCounselors } from '../../api/students';
import { useBranchFilter } from '../../context/branchContext';
import { PdfViewerDialog, PdfViewerSource } from '../../components/pdf/PdfViewerDialog';
import { APPLICATION_STATUS_OPTIONS, FILTER_LABELS, PAYMENT_STATUSES, ReportDefinition, ReportFilterKey } from './reportCatalog';

type Option = { value: number | string; label: string };

const toIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Quick periods for the date range. */
const PRESETS: Array<{ label: string; range: () => [string, string] }> = [
  {
    label: 'This month',
    range: () => {
      const now = new Date();
      return [toIso(new Date(now.getFullYear(), now.getMonth(), 1)), toIso(now)];
    },
  },
  {
    label: 'Last month',
    range: () => {
      const now = new Date();
      return [toIso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), toIso(new Date(now.getFullYear(), now.getMonth(), 0))];
    },
  },
  {
    label: 'This year',
    range: () => {
      const now = new Date();
      return [toIso(new Date(now.getFullYear(), 0, 1)), toIso(now)];
    },
  },
];

const useLookupOptions = (path: string, enabled: boolean) =>
  useQuery({
    queryKey: ['lookup', path, 'reports'],
    queryFn: () => listLookup(path),
    enabled,
    staleTime: 5 * 60_000,
    select: (rows): Option[] =>
      rows.map((r) => ({ value: r.id as number, label: `${String(r.name)}${r.isActive === false ? ' (inactive)' : ''}` })),
  });

/** One report: its filters, then Preview PDF (in-app viewer), Excel or CSV.
 * Everything is filtered and permission-checked by the API. */
export const ReportRunDialog = ({ report, onClose }: { report: ReportDefinition | null; onClose: () => void }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { branches, selectedBranchId } = useBranchFilter();
  const has = (key: ReportFilterKey) => !!report?.filters.includes(key);

  const [filters, setFilters] = useState<ReportFilters>({});
  const [busy, setBusy] = useState<'xlsx' | 'csv' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<PdfViewerSource | null>(null);

  useEffect(() => {
    setFilters(selectedBranchId ? { branchId: selectedBranchId } : {});
    setError(null);
  }, [report, selectedBranchId]);

  const universities = useLookupOptions('/master-data/universities', has('university'));
  const intakes = useLookupOptions('/master-data/intakes', has('intake'));
  const leadStatuses = useLookupOptions('/master-data/lead-statuses', has('leadStatus'));
  const leadSources = useLookupOptions('/master-data/lead-sources', has('leadSource'));
  const paymentCategories = useLookupOptions('/master-data/payment-categories', has('paymentCategory'));
  const visaStatuses = useLookupOptions('/master-data/visa-statuses', has('visaStatus'));
  const counselors = useQuery({
    queryKey: ['students', 'counselors', 'reports'],
    queryFn: () => listStudentCounselors(),
    enabled: has('counselor'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const set = (key: keyof ReportFilters, value: string | number | undefined) =>
    setFilters((f) => ({ ...f, [key]: value === '' ? undefined : value }));

  const select = (key: keyof ReportFilters, label: string, options: Option[] | undefined, allLabel: string) =>
    // A list the role can't load (e.g. no Master Data access) is simply
    // not offered; the report still runs without that filter.
    options ? (
      <TextField
        select
        size="small"
        fullWidth
        label={label}
        value={filters[key] ?? ''}
        onChange={(e) => set(key, typeof options[0]?.value === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value)}
      >
        <MenuItem value="">
          <em>{allLabel}</em>
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    ) : null;

  const download = async (format: 'xlsx' | 'csv') => {
    if (!report) return;
    setBusy(format);
    setError(null);
    try {
      await downloadReport(report.type, format, filters);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The report could not be generated.');
    } finally {
      setBusy(null);
    }
  };

  const preview = () => {
    if (!report) return;
    const snapshot = { ...filters };
    setViewer({ title: report.name, filename: reportFilename(report.type, 'pdf'), load: () => fetchReportPdf(report.type, snapshot) });
  };

  const dateInvalid = !!filters.dateFrom && !!filters.dateTo && filters.dateFrom > filters.dateTo;

  return (
    <>
      <Dialog open={!!report} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {report?.name}
          <Typography variant="body2" color="text.secondary">
            {report?.description}
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {has('dateRange') && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reporting period{report?.dateLabel ? ` — ${report.dateLabel.toLowerCase()}` : ''}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ my: 1 }} flexWrap="wrap" useFlexGap>
                  {PRESETS.map((p) => (
                    <Chip
                      key={p.label}
                      label={p.label}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const [from, to] = p.range();
                        setFilters((f) => ({ ...f, dateFrom: from, dateTo: to }));
                      }}
                    />
                  ))}
                  <Chip label="All dates" size="small" variant="outlined" onClick={() => setFilters((f) => ({ ...f, dateFrom: undefined, dateTo: undefined }))} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.dateFrom ?? ''}
                    onChange={(e) => set('dateFrom', e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.dateTo ?? ''}
                    onChange={(e) => set('dateTo', e.target.value)}
                    error={dateInvalid}
                    helperText={dateInvalid ? '“To” is before “From”.' : undefined}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Stack>
              </Box>
            )}
            {has('branch') &&
              branches.length > 0 &&
              select('branchId', FILTER_LABELS.branch, branches.map((b) => ({ value: b.id, label: b.name })), 'All branches')}
            {has('counselor') &&
              select(
                'counselorId',
                report?.type === 'leads' ? 'Assigned counsellor' : 'Registered by (counsellor)',
                counselors.data?.map((c) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` })),
                'All counsellors',
              )}
            {has('university') && select('universityId', FILTER_LABELS.university, universities.data, 'All universities')}
            {has('intake') && select('intakeId', FILTER_LABELS.intake, intakes.data, 'All intakes')}
            {has('leadStatus') && select('leadStatusId', FILTER_LABELS.leadStatus, leadStatuses.data, 'All statuses')}
            {has('leadSource') && select('leadSourceId', FILTER_LABELS.leadSource, leadSources.data, 'All sources')}
            {has('paymentCategory') && select('paymentCategoryId', FILTER_LABELS.paymentCategory, paymentCategories.data, 'All categories')}
            {has('paymentStatus') && select('paymentStatus', FILTER_LABELS.paymentStatus, PAYMENT_STATUSES, 'All statuses')}
            {has('applicationStatus') && select('applicationStatus', FILTER_LABELS.applicationStatus, APPLICATION_STATUS_OPTIONS, 'All statuses')}
            {has('visaStatus') && select('visaStatusId', FILTER_LABELS.visaStatus, visaStatuses.data, 'All visa statuses')}
            {report && report.filters.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                This report has no filters; it covers the branches you have access to.
              </Typography>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, py: 2 }}>
          <Button
            startIcon={busy === 'csv' ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={!!busy || dateInvalid}
            onClick={() => void download('csv')}
          >
            CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={busy === 'xlsx' ? <CircularProgress size={16} /> : <TableViewIcon />}
            disabled={!!busy || dateInvalid}
            onClick={() => void download('xlsx')}
          >
            Excel
          </Button>
          <Button variant="contained" startIcon={<PictureAsPdfIcon />} disabled={!!busy || dateInvalid} onClick={preview}>
            Preview PDF
          </Button>
        </DialogActions>
      </Dialog>
      <PdfViewerDialog source={viewer} onClose={() => setViewer(null)} />
    </>
  );
};
